import React, { useEffect, useId, useRef, useState } from 'react';
import axios from 'axios';
import { Camera, Plus, Trash2, X } from 'lucide-react';
import { apiBase, authHeaders, getToken, resolveOrgId } from '../utils/authHeaders';
import { decode } from '../scan/barcodeDecoder';
import { apiAllowed, useExperience } from '../experience/experience';

// ck_ops/access.py: read includes viewers, but driver/api_client veto even a second held role.
const CK_READ_ROLES = ['org_owner', 'org_admin', 'manager', 'staff', 'viewer', 'accountant'];

export const ckApi = axios.create({ baseURL: `${apiBase()}/api/ck-ops` });
export function readCkScope() {
  return { orgId: resolveOrgId(), token: getToken(), locationId: localStorage.getItem('current_location_id') || '', department: localStorage.getItem('active_department') || '' };
}
export function useCkScope(refreshToken) {
  const [scope, setScope] = useState(readCkScope);
  useEffect(() => {
    const update = () => { const next = readCkScope(); setScope(previous => scopeKey(previous) === scopeKey(next) ? previous : next); };
    update();
    const events = ['organizationChanged', 'locationChanged', 'fi-department-changed', 'storage'];
    events.forEach(e => window.addEventListener(e, update));
    return () => events.forEach(e => window.removeEventListener(e, update));
  }, [refreshToken]);
  return scope;
}
export const scopeKey = scope => JSON.stringify(scope);
export const requestConfig = (scope, signal) => ({ headers: authHeaders(scope), signal });
export function useCkNavEnabled(token, probe = true) {
  const scope = useCkScope(token);
  const exp = useExperience();
  const roles = exp.roles || [];
  const roleKey = JSON.stringify(roles);
  const mayProbe = probe && apiAllowed(exp, '/api/ck-ops/capabilities')
    && (!exp.organizationId || exp.organizationId === scope.orgId)
    && roles.some(role => CK_READ_ROLES.includes(role))
    && !roles.some(role => role === 'driver' || role === 'api_client');
  const key = JSON.stringify([scopeKey(scope), roleKey, exp.organizationId]);
  const [allowedKey, setAllowedKey] = useState('');
  useEffect(() => {
    const controller = new AbortController();
    setAllowedKey('');
    if (mayProbe && scope.orgId && scope.locationId && scope.token) {
      ckApi.get('/capabilities', requestConfig(scope, controller.signal))
        .then(() => { if (!controller.signal.aborted) setAllowedKey(key); })
        .catch(() => {});
    }
    return () => controller.abort();
  }, [key, scope, mayProbe]);
  return mayProbe && allowedKey === key;
}
export function parseLotPayload(value) {
  const match = /^LOT:([^\s\x00-\x1f]+)$/.exec(value.trim());
  if (!match) throw new Error('Enter a LOT: barcode payload.');
  return match[1];
}
// G-LOT-2: what the labels print — LOT:<id> (lot label), L-XXXXXXXX-C (a pack label's short lot code) and
// UNIT:U-XXXXXXXX-C (a unit label). The shape is checked here; the server says which lot, if any, it names.
const SHORT_LABEL = /^(?:UNIT:\s*)?[LUB]-[0-9A-Z]{8}-[0-9A-Z*~$=]$/i;
export function parseLabelPayload(value) {
  const text = (value || '').trim();
  if (SHORT_LABEL.test(text)) return text;
  try { parseLotPayload(text); return text; } catch { throw new Error('Not a lot label. Scan the barcode on a lot, pack or unit label.'); }
}
const isLabel = raw => { try { parseLabelPayload(raw); return true; } catch { return false; } };
export function Field({ label, options, datalist, rowIndex, ...props }) {
  // Called on every render (Rules of Hooks): a conditional call fails the production build's lint step.
  const uid = useId().replace(/[^a-zA-Z0-9_-]/g, '');
  const suffix = rowIndex !== undefined ? `-${rowIndex}` : '';
  const listId = datalist && datalist.length ? `dl-${uid}-${String(label).replace(/[^a-zA-Z0-9_-]/g, '-')}${suffix}` : undefined;
  return <label className="ck-field"><span>{label}</span>{options ? <select aria-label={label} {...props}>{options.map(o => <option key={o.value ?? o} value={o.value ?? o}>{o.label ?? o}</option>)}</select> : <><input aria-label={label} list={listId} {...props} />{listId && <datalist id={listId}>{datalist.map(o => <option key={o.value ?? o} value={o.value ?? o}>{o.label ?? o}</option>)}</datalist>}</>}</label>;
}
export const decimalProps = { type: 'text', inputMode: 'decimal', pattern: '[0-9]+([.][0-9]+)?' };
// PFM-22: why a transfer arrived short. The same list as ck_ops.models.TransitLossReason.
export const lossReasonOptions = [
  { value: '', label: 'No loss' },
  { value: 'spilled', label: 'Spilled' },
  { value: 'damaged', label: 'Damaged' },
  { value: 'missing', label: 'Missing' },
  { value: 'temperature', label: 'Temperature' },
  { value: 'refused', label: 'Refused' },
  { value: 'other', label: 'Other (add a note)' },
];
// What stops a transfer receipt being sent, or '' when it can go. `shipped` maps lot id to the shipped quantity.
// The server checks all of this again; this only saves a round trip.
export function receiptProblem(lines, shipped) {
  for (const line of lines) {
    const received = Number(String(line.received_qty ?? '').trim());
    const sent = Number(shipped[line.lot_id]);
    if (String(line.received_qty ?? '').trim() === '' || !Number.isFinite(received) || received < 0) return `Enter the quantity received for lot ${line.lot_id}.`;
    if (Number.isFinite(sent) && received > sent) return `Lot ${line.lot_id}: received cannot be more than the ${sent} shipped.`;
    if (Number.isFinite(sent) && received < sent && !line.loss_reason_code) return `Lot ${line.lot_id}: choose a loss reason for the short receipt.`;
    if (line.loss_reason_code === 'other' && !String(line.loss_reason || '').trim()) return `Lot ${line.lot_id}: add a note for the other reason.`;
  }
  return '';
}
// The receipt line the transfer receive route takes: the code only when the line is short.
export function receiptLine(line, shipped) {
  const short = Number(String(line.received_qty).trim()) < Number(shipped[line.lot_id]);
  return {
    lot_id: line.lot_id,
    received_qty: String(line.received_qty).trim(),
    loss_reason_code: short ? line.loss_reason_code || null : null,
    loss_reason: short ? String(line.loss_reason || '').trim() || null : null,
  };
}
export function commandId() {
  const bytes = new Uint8Array(16);
  window.crypto.getRandomValues(bytes);
  return `ck-${Array.from(bytes, b => b.toString(16).padStart(2, '0')).join('')}`;
}
export function Form({ title, submit, onSubmit, busy, submitDisabled, children }) {
  return <form className="ck-form" aria-label={title} onSubmit={e => { e.preventDefault(); onSubmit(); }}><h2>{title}</h2><fieldset disabled={busy}><div className="ck-fields">{children}</div><button className="ck-primary" type="submit" disabled={busy || submitDisabled}>{submit}</button></fieldset></form>;
}
// A field may name its own control (`as`, e.g. a Picker) and props that depend on its row (`rowProps(row)`).
export function Rows({ rows, setRows, fields, blank, label, allowAdd = true }) {
  return <div className="ck-rows"><h3>{label}</h3>{rows.map((row, index) => <div className="ck-line" key={index}>{fields.map(({ key, as: Control = Field, rowProps, ...field }) => <Control key={key} {...field} {...(rowProps ? rowProps(row) : {})} rowIndex={index} label={`${field.label} ${index + 1}`} value={row[key] ?? ''} onChange={e => setRows(rows.map((r, i) => i === index ? { ...r, [key]: e.target.value } : r))} />)}<button type="button" title="Remove line" aria-label={`Remove ${label} ${index + 1}`} disabled={rows.length === 1} onClick={() => setRows(rows.filter((_, i) => i !== index))}><Trash2 size={16} /></button></div>)}{allowAdd && <button type="button" onClick={() => setRows([...rows, { ...blank }])}><Plus size={16} /> Add line</button>}</div>;
}
export function Table({ headers, children, empty }) {
  return <div className="ck-table-wrap" tabIndex={0} role="region" aria-label={`${headers[0]} records`}><table><thead><tr>{headers.map(h => <th key={h}>{h}</th>)}</tr></thead><tbody>{children}</tbody></table>{empty && <p className="ck-empty">No records at this site.</p>}</div>;
}
// LOT:<id> gives onScan the lot id, as before. With onLabel, the other printed payloads (a pack label's L- code, a
// unit's UNIT:U- code) go to it as scanned and the server resolves them; without it they are refused.
export function LotScanner({ onScan, onLabel, busy }) {
  const [value, setValue] = useState('');
  const [camera, setCamera] = useState(false);
  const [error, setError] = useState('');
  const video = useRef(null);
  const callback = useRef(onScan);
  callback.current = onScan;
  const labelCallback = useRef(onLabel);
  labelCallback.current = onLabel;
  useEffect(() => {
    if (!camera) return undefined;
    let stopped = false, stream, timer;
    const stop = () => { stopped = true; clearTimeout(timer); stream?.getTracks().forEach(t => t.stop()); };
    (async () => {
      try {
        if (!navigator.mediaDevices?.getUserMedia) throw new Error('Camera scanning is unavailable. Enter the LOT: payload below.');
        stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: 'environment' }, audio: false });
        if (stopped) { stream.getTracks().forEach(t => t.stop()); return; }
        video.current.srcObject = stream;
        await video.current.play();
        const scan = async () => {
          if (stopped) return;
          try {
            const raw = await decode(video.current);
            if (stopped) return;
            if (raw && /^LOT:/.test(raw)) { const id = parseLotPayload(raw); stop(); setCamera(false); setValue(raw); callback.current(id); return; }
            if (raw && labelCallback.current && isLabel(raw)) { stop(); setCamera(false); setValue(raw); labelCallback.current(parseLabelPayload(raw)); return; }
            timer = setTimeout(scan, 250);
          } catch { if (!stopped) { stop(); setCamera(false); setError('Camera scanning failed. Enter the LOT: payload below.'); } }
        };
        scan();
      } catch (e) { if (!stopped) { stop(); setCamera(false); setError(e.message || 'Camera access denied. Enter the LOT: payload below.'); } }
    })();
    return stop;
  }, [camera]);
  return <section className="ck-scan"><form onSubmit={e => { e.preventDefault(); try { setError(''); if (onLabel && !/^LOT:/.test(value.trim())) onLabel(parseLabelPayload(value)); else onScan(parseLotPayload(value)); } catch (err) { setError(err.message); } }}><Field label="Lot barcode" data-testid="lot-scan-input" value={value} onChange={e => setValue(e.target.value)} required /><button disabled={busy} type="submit" data-testid="lot-scan-submit">Find lot</button><button type="button" disabled={busy} onClick={() => { setError(''); setCamera(!camera); }}>{camera ? <X size={18} /> : <Camera size={18} />}{camera ? 'Stop camera' : 'Scan'}</button></form>{camera && <video ref={video} muted playsInline aria-label="Lot barcode camera" />}{error && <p role="alert">{error}</p>}</section>;
}
